import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PageSettings, ShoppableFeedService } from '@vendasta/shoppable-feed';

// Local interface for posts used in this component
interface ExtendedPagePost {
  pageId: string;
  postId: string;
  permalink: string;
  imageUrl?: string;
  media?: string[];
  // URLs of media that are known to be videos (from API metadata)
  videoUrls?: string[];
  // Map of media URL to its mime type (if provided by API)
  mediaMimeByUrl?: { [url: string]: string };
  // Precomputed properties for template performance
  displayMedia?: string[];
  isCarousel?: boolean;
  isSingleVideo?: boolean;
  isVideoByUrl?: { [url: string]: boolean };
  resolvedMimeByUrl?: { [url: string]: string | undefined };
}

@Component({
  selector: 'app-feed',
  templateUrl: './shoppable-feed.component.html',
  styleUrls: ['./shoppable-feed.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShoppableFeedComponent implements OnInit, OnDestroy {
  pagePosts: ExtendedPagePost[];
  pageSettings: PageSettings;
  carouselIndices: { [postId: string]: number } = {};
  videoPlayingStates: { [postId: string]: boolean } = {};
  private lastMediaOpenTimestampMs = 0;
  private videoObserver: IntersectionObserver | null = null;
  private mimeTypeCache: Map<string, string> = new Map();

  constructor(
    private shoppableFeedService: ShoppableFeedService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    // accounts are configured via subdomain, which we use to fetch the posts
    // example: https://fashionbyjane.clickable.bio/ for `fashionbyjane` account
    const urlParts = window.location.href.split('.');
    const domainName = urlParts[0].replace('https://', '');
    this.shoppableFeedService.listPostsByDomainName(domainName, '', 1000).subscribe((resp) => {
      const mapped: ExtendedPagePost[] = (resp.posts || []).map((p: any) => this.mapPostToExtended(p));

      // Keep posts that have either a primary image or at least one media entry
      this.pagePosts = mapped.filter(
        (post) => (!!post.imageUrl && post.imageUrl.length > 0) || (post.media && post.media.length > 0),
      );
      this.pageSettings = resp.pageSettings;
    });
  }

  private mapPostToExtended(p: any): ExtendedPagePost {
    const mediaItems: any[] = Array.isArray(p.media) ? p.media : [];
    const mediaUrls: string[] = mediaItems.map((m: any) => m?.url).filter((u: string) => !!u);
    const mediaMimeByUrl: { [url: string]: string } = {};
    mediaItems.forEach((m: any) => {
      const url = m?.url;
      const type = String(m?.type || m?.mimeType || '').trim();
      if (url && type) {
        mediaMimeByUrl[url] = type;
      }
    });
    // Detect videos using API-provided type/mimeType when available
    const videoUrls: string[] = mediaItems
      .filter((m: any) => {
        const type = String(m?.type || m?.mimeType || '').toLowerCase();
        return type.includes('video');
      })
      .map((m: any) => m?.url)
      .filter((u: string) => !!u);

    const imageUrl: string | undefined = p.imageUrl || mediaUrls[0];
    const displayMedia: string[] = mediaUrls.length > 0 ? mediaUrls : imageUrl ? [imageUrl] : [];
    const isVideoByUrl: { [url: string]: boolean } = {};
    displayMedia.forEach((u) => {
      isVideoByUrl[u] = videoUrls.includes(u) || this.isVideo(u);
    });
    const resolvedMimeByUrl: { [url: string]: string | undefined } = {};
    displayMedia.forEach((u) => {
      const rawType = mediaMimeByUrl[u];
      resolvedMimeByUrl[u] = this.resolveMimeForUrl(rawType, u);
    });

    const post: ExtendedPagePost = {
      pageId: p.pageId,
      postId: p.postId,
      permalink: p.permalink,
      imageUrl,
      media: mediaUrls,
      videoUrls,
      mediaMimeByUrl,
      displayMedia,
      isCarousel: mediaUrls.length > 1,
      isSingleVideo: displayMedia.length === 1 && isVideoByUrl[displayMedia[0]] === true,
      isVideoByUrl,
      resolvedMimeByUrl,
    } as ExtendedPagePost;

    // Attempt to resolve missing MIME types via a HEAD request (cached)
    displayMedia.forEach((u) => {
      if (post.resolvedMimeByUrl && post.resolvedMimeByUrl[u] === undefined) {
        this.tryResolveMimeFromHead(u, post);
      }
    });

    return post;
  }

  ngOnDestroy(): void {
    // Clear video playing states
    this.videoPlayingStates = {};
    this.carouselIndices = {};
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
  }

  onButtonClick(): void {
    window.open(this.pageSettings.buttonLink, '_blank');
  }

  // Check if URL is a video
  isVideo(url: any): boolean {
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.m4v', '.m3u8', '.ogv', '.ogg'];
    const path = this.getUrlPathname(String(url ?? ''));
    if (!path) {
      return false;
    }
    return videoExtensions.some((ext) => path.endsWith(ext));
  }

  // Prefer API metadata: determine if the given media URL for a post is a video
  isVideoForPost(post: ExtendedPagePost, url: any): boolean {
    const normalizedUrl = String(url ?? '');
    if (!normalizedUrl) return false;
    if (post.videoUrls && post.videoUrls.includes(normalizedUrl)) {
      return true;
    }
    return this.isVideo(normalizedUrl);
  }

  // Retrieve mime type for a media URL if available
  getMimeType(post: ExtendedPagePost, url: any): string | undefined {
    const normalizedUrl = String(url ?? '');
    if (!normalizedUrl) return undefined;
    // Prefer pre-resolved result if available
    const pre = post.resolvedMimeByUrl ? post.resolvedMimeByUrl[normalizedUrl] : undefined;
    if (pre !== undefined) return pre;
    const raw = post.mediaMimeByUrl ? post.mediaMimeByUrl[normalizedUrl] : undefined;
    return this.resolveMimeForUrl(raw, normalizedUrl);
  }

  // Check if post should use carousel (has multiple media or contains video)
  shouldUseCarousel(post: ExtendedPagePost): boolean {
    if (!post.media || post.media.length === 0) return false;
    // Use carousel only when there are multiple media items
    return post.media.length > 1;
  }

  // Get current media for display (fallback to imageUrl if no media)
  getMediaUrls(post: ExtendedPagePost): string[] {
    if (post.media && post.media.length > 0) {
      return post.media;
    }
    return post.imageUrl ? [post.imageUrl] : [];
  }

  // Get current carousel index for a post
  getCurrentIndex(postId: string): number {
    return this.carouselIndices[postId] || 0;
  }

  // Set current carousel index for a post
  setCurrentIndex(postId: string, index: number): void {
    this.carouselIndices[postId] = index;
  }

  // Navigate to previous media
  navigatePrev(postId: string): void {
    const currentIndex = this.getCurrentIndex(postId);
    if (currentIndex > 0) {
      this.setCurrentIndex(postId, currentIndex - 1);
    }
  }

  // Navigate to next media
  navigateNext(postId: string): void {
    const post = this.pagePosts.find((p) => p.postId === postId);
    if (post) {
      const currentIndex = this.getCurrentIndex(postId);
      const maxIndex = this.getMediaUrls(post).length - 1;
      if (currentIndex < maxIndex) {
        this.setCurrentIndex(postId, currentIndex + 1);
      }
    }
  }

  // Navigate carousel (legacy method for compatibility)
  navigateCarousel(_direction: 'prev' | 'next', _postId: string): void {
    // This will be handled by the specific navigation methods above
  }

  // Handle media click
  onMediaClick(post: ExtendedPagePost, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Pause any playing video before navigating
    this.pauseOtherVideos();

    const now = Date.now();
    if (now - this.lastMediaOpenTimestampMs < 300) {
      return;
    }
    this.lastMediaOpenTimestampMs = now;

    // Small delay to ensure it's not a scroll/swipe gesture
    setTimeout(() => {
      window.open(post.permalink, '_blank');
    }, 50);
  }

  // Check if video is playing
  isVideoPlaying(postId: string): boolean {
    return this.videoPlayingStates[postId] || false;
  }

  // Handle video can play event
  onVideoCanPlay(event: Event): void {
    const video = event.target as HTMLVideoElement;
    const postId = video.getAttribute('data-post-id');

    if (!postId) return;

    // Observe visibility and only play when sufficiently visible
    this.ensureVideoObserver();
    this.videoObserver!.observe(video);
  }

  // Attempt to autoplay video
  private attemptAutoplay(video: HTMLVideoElement, postId: string): void {
    // Set initial state
    this.videoPlayingStates[postId] = false;

    // Pause other videos first to avoid race conditions that cause AbortError
    this.pauseOtherVideos(postId);

    // Try to play the video
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Autoplay started successfully
          this.videoPlayingStates[postId] = true;
        })
        .catch((error) => {
          // Ignore AbortError (occurs when play() is interrupted by pause())
          if (error && (error.name === 'AbortError' || String(error).includes('AbortError'))) {
            return;
          }
          console.log('Autoplay failed for video', postId, error);
          // Autoplay failed - keep play indicator visible
          this.videoPlayingStates[postId] = false;
        });
    }

    // We avoid attaching per-video play/pause listeners to keep DOM listeners minimal.
  }

  // Force play video when user clicks play button
  forcePlayVideo(postId: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    // Prefer the video inside the clicked container to avoid selecting a hidden slide
    const targetElement = event.currentTarget as HTMLElement | null;
    const container = targetElement?.closest('.video-container') as HTMLElement | null;
    const scopedVideo = container?.querySelector('video') as HTMLVideoElement | null;

    const video = scopedVideo || (document.querySelector(`video[data-post-id="${postId}"]`) as HTMLVideoElement | null);

    if (video) {
      // Check basic support for the declared source type, if available
      const sourceEl = video.querySelector('source') as HTMLSourceElement | null;
      const declaredType = sourceEl?.type;
      if (declaredType) {
        const support = video.canPlayType(declaredType);
        if (!support) {
          console.warn('Browser reports it cannot play this MIME type:', declaredType);
        }
      }

      this.pauseOtherVideos(postId);
      video
        .play()
        .then(() => {
          this.videoPlayingStates[postId] = true;
        })
        .catch((error) => {
          // Ignore AbortError caused by an immediate pause elsewhere
          if (error && (error.name === 'AbortError' || String(error).includes('AbortError'))) {
            return;
          }
          console.error('Failed to play video:', error);
          this.videoPlayingStates[postId] = false;
        });
    }
  }

  // Expose an explicit pause handler we can call on button clicks if ever needed
  forcePauseVideo(postId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const video = document.querySelector(`video[data-post-id="${postId}"]`) as HTMLVideoElement | null;
    if (video) {
      video.pause();
      this.videoPlayingStates[postId] = false;
    }
  }

  private pauseOtherVideos(exceptPostId?: string): void {
    const videos = Array.from(document.querySelectorAll('video[data-post-id]')) as HTMLVideoElement[];
    videos.forEach((v) => {
      const id = v.getAttribute('data-post-id') || '';
      if (!exceptPostId || id !== exceptPostId) {
        if (!v.paused) {
          v.pause();
        }
        if (id) {
          this.videoPlayingStates[id] = false;
        }
      }
    });
  }

  private ensureVideoObserver(): void {
    if (this.videoObserver) return;
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          const postId = video.getAttribute('data-post-id');
          if (!postId) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Play this video and pause the rest
            this.pauseOtherVideos(postId);
            this.attemptAutoplay(video, postId);
          } else {
            // Not sufficiently visible; pause it
            video.pause();
            this.videoPlayingStates[postId] = false;
          }
        });
      },
      { threshold: [0, 0.5, 1] },
    );
  }

  private resolveMimeForUrl(rawType: string | undefined, url: string): string | undefined {
    const declared = (rawType || '').trim().toLowerCase();
    if (declared && declared.includes('/')) {
      return declared;
    }
    if (declared && declared.includes('video')) {
      const inferred = this.inferVideoMimeFromPath(url);
      if (inferred) return inferred;
      return undefined;
    }
    return this.inferVideoMimeFromPath(url);
  }

  private tryResolveMimeFromHead(url: string, post: ExtendedPagePost): void {
    if (!url) return;
    const cached = this.mimeTypeCache.get(url);
    if (cached) {
      if (!post.resolvedMimeByUrl) post.resolvedMimeByUrl = {};
      post.resolvedMimeByUrl[url] = cached;
      if (!post.isVideoByUrl) post.isVideoByUrl = {};
      post.isVideoByUrl[url] = cached.toLowerCase().startsWith('video/');
      return;
    }
    this.http.head(url, { observe: 'response' }).subscribe({
      next: (resp) => {
        const ct = resp.headers.get('Content-Type') || resp.headers.get('content-type') || '';
        const normalized = ct.trim().toLowerCase();
        if (normalized) {
          this.mimeTypeCache.set(url, normalized);
          if (!post.resolvedMimeByUrl) post.resolvedMimeByUrl = {};
          post.resolvedMimeByUrl[url] = normalized;
          if (!post.isVideoByUrl) post.isVideoByUrl = {};
          post.isVideoByUrl[url] = normalized.startsWith('video/');
        }
      },
      error: () => {
        // Ignore CORS or network errors; browser will rely on response headers during playback
      },
    });
  }

  private getUrlPathname(inputUrl: string): string {
    const lower = (inputUrl || '').trim();
    if (!lower) return '';
    try {
      const u = new URL(lower);
      return (u.pathname || '').toLowerCase();
    } catch {
      // Fallback: strip query/hash and return last path segment
      const noHash = lower.split('#')[0];
      const noQuery = noHash.split('?')[0];
      return noQuery.toLowerCase();
    }
  }

  private inferVideoMimeFromPath(inputUrl: string): string | undefined {
    const path = this.getUrlPathname(inputUrl);
    if (!path) return undefined;
    if (path.endsWith('.mp4')) return 'video/mp4';
    if (path.endsWith('.webm')) return 'video/webm';
    if (path.endsWith('.ogv') || path.endsWith('.ogg')) return 'video/ogg';
    if (path.endsWith('.mov')) return 'video/quicktime';
    if (path.endsWith('.m3u8')) return 'application/vnd.apple.mpegurl';
    if (path.endsWith('.avi')) return 'video/x-msvideo';
    if (path.endsWith('.wmv')) return 'video/x-ms-wmv';
    if (path.endsWith('.flv')) return 'video/x-flv';
    if (path.endsWith('.m4v')) return 'video/x-m4v';
    return undefined;
  }
}
